// ODION belt builder: buckle -> strap -> one bundle line in the cart.
// Talks to the theme only through Horizon's cart event, so the cart icon and drawer refresh.
import { CartAddEvent } from '@theme/events';

const GROUPS = ['Leather', 'Contrast stitch', 'Suede', 'Fabric'];
const groupOf = (title) => {
  if (/suede/i.test(title)) return 'Suede';
  if (/contrast stitch/i.test(title)) return 'Contrast stitch';
  if (/polyester|nylon|canvas/i.test(title)) return 'Fabric';
  return 'Leather';
};
const shortName = (title) => title.replace(/ Contrast Stitch| Suede/gi, '').replace(/^Tanned Leather /i, 'Tanned ');
const money = (cents) => '$' + (cents / 100).toFixed(2);
const el = (tag, cls, text) => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
};

class ObBuilder extends HTMLElement {
  connectedCallback() {
    const data = JSON.parse(this.querySelector('[data-belts]').textContent);
    this.belts = new Map(data.belts.map((b) => [b.handle, b]));
    this.strapPrices = data.strapPrices || {};
    this.bucklePrice = Number(this.dataset.bucklePrice) || 0;
    this.buckle = null;
    this.variant = null;

    this.strapsEl = this.querySelector('[data-straps]');
    this.emptyEl = this.querySelector('[data-empty]');
    this.ui = {
      pic: this.querySelector('[data-pic]'),
      name: this.querySelector('[data-name]'),
      strap: this.querySelector('[data-strap]'),
      save: this.querySelector('[data-save]'),
      price: this.querySelector('[data-price]'),
      add: this.querySelector('[data-add]'),
      addLabel: this.querySelector('[data-add-label]'),
      error: this.querySelector('[data-error]'),
    };

    this.querySelectorAll('.ob-buckle__btn').forEach((btn) =>
      btn.addEventListener('click', () => this.pickBuckle(btn.dataset.handle, true))
    );
    this.querySelectorAll('.ob-tab').forEach((btn) => btn.addEventListener('click', () => this.filter(btn)));
    this.ui.add.addEventListener('click', () => this.addToCart());

    // The pinned phone button only shows while the builder itself is on screen.
    new IntersectionObserver(([entry]) => this.classList.toggle('is-in-view', entry.isIntersecting)).observe(
      this.querySelector('.ob-layout')
    );

    // ?style=western opens on that tab (style cards on the Railtek page link here).
    const params = new URLSearchParams(location.search);
    const style = (params.get('style') || '').toLowerCase();
    const styleTab = style && [...this.querySelectorAll('.ob-tab')].find((t) => t.textContent.trim().toLowerCase() === style);
    const first = styleTab || this.querySelector('.ob-tab.is-on');
    if (first) this.filter(first);

    // Links like ?buckle=brushed-steel-railtek-belt&strap=Black open with a belt chosen.
    const start = params.get('buckle');
    this.preferStrap = params.get('strap');
    if (start && this.belts.has(start)) {
      // Open on the buckle's own tab (or All) so the chosen buckle is in view.
      const li = this.querySelector(`.ob-buckle__btn[data-handle="${CSS.escape(start)}"]`)?.closest('.ob-buckle');
      if (li && li.hidden) {
        const lanes = li.dataset.lanes.split(' ');
        const tab = [...this.querySelectorAll('.ob-tab')].find((t) => lanes.includes(t.dataset.lane)) ||
          this.querySelector('.ob-tab[data-lane="all"]');
        if (tab) this.filter(tab);
      }
      this.pickBuckle(start, false);
    }
  }

  filter(btn) {
    const lane = btn.dataset.lane;
    this.querySelectorAll('.ob-tab').forEach((b) => {
      const on = b === btn;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
    });
    this.querySelectorAll('.ob-buckle').forEach((li) => {
      li.hidden = lane !== 'all' && !li.dataset.lanes.split(' ').includes(lane);
    });
  }

  pickBuckle(handle, scroll) {
    const belt = this.belts.get(handle);
    if (!belt) return;
    this.buckle = belt;
    this.querySelectorAll('.ob-buckle__btn').forEach((b) => {
      const on = b.dataset.handle === handle;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
    });

    // Keep the same strap when the new buckle offers it.
    const wanted = this.variant ? this.variant.title : this.preferStrap;
    this.variant = (wanted && belt.variants.find((v) => v.title.toLowerCase() === wanted.toLowerCase())) || null;
    this.renderStraps();
    this.renderSummary();
    if (scroll && window.matchMedia('(max-width: 989px)').matches) {
      this.querySelector('[data-step="2"]').scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  renderStraps() {
    this.emptyEl.hidden = true;
    this.strapsEl.hidden = false;
    this.strapsEl.replaceChildren();
    const byGroup = new Map(GROUPS.map((g) => [g, []]));
    this.buckle.variants.forEach((v) => byGroup.get(groupOf(v.title)).push(v));

    byGroup.forEach((list, group) => {
      if (!list.length) return;
      const wrap = el('div', 'ob-strap-group');
      const head = el('h3', 'ob-strap-group__name', group);
      head.append(el('span', 'ob-strap-group__price', money(Math.min(...list.map((v) => v.price)))));
      wrap.append(head);
      const ul = el('ul', 'ob-strap-list');
      ul.setAttribute('role', 'list');
      list.forEach((v) => {
        const li = el('li');
        const b = el('button', 'ob-strap');
        b.type = 'button';
        b.dataset.id = v.id;
        const on = this.variant && this.variant.id === v.id;
        b.classList.toggle('is-on', Boolean(on));
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.setAttribute('aria-label', `${v.title}, ${money(v.price)}`);
        if (v.image) {
          const img = el('img', 'ob-strap__img');
          img.src = v.image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 250;
          img.height = 250;
          b.append(img);
        }
        b.append(el('span', 'ob-strap__name', shortName(v.title)));
        b.addEventListener('click', () => this.pickStrap(v));
        li.append(b);
        ul.append(li);
      });
      wrap.append(ul);
      this.strapsEl.append(wrap);
    });
  }

  pickStrap(v) {
    this.variant = v;
    this.strapsEl.querySelectorAll('.ob-strap').forEach((b) => {
      const on = Number(b.dataset.id) === v.id;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
    });
    this.renderSummary();
  }

  renderSummary() {
    const { ui, buckle, variant } = this;
    ui.error.hidden = true;
    ui.name.textContent = buckle ? buckle.name : 'Not chosen';
    ui.strap.textContent = variant
      ? variant.title
      : buckle
        ? 'Choose a strap'
        : this.preferStrap
          ? `${this.preferStrap} (now choose a buckle)`
          : 'Not chosen';

    const src = variant ? variant.image : buckle ? buckle.image : null;
    ui.pic.replaceChildren();
    if (src) {
      const img = el('img');
      img.src = src;
      img.alt = '';
      ui.pic.append(img);
    }

    let label = '';
    if (variant) label = money(variant.price);
    else if (buckle) label = `From ${money(Math.min(...buckle.variants.map((v) => v.price)))}`;
    ui.price.textContent = label;
    ui.price.hidden = !label;

    const strapAlone = variant ? this.strapPrices[variant.title] : undefined;
    const savings = variant && strapAlone ? this.bucklePrice + strapAlone - variant.price : 0;
    ui.save.hidden = !(savings > 0);
    if (savings > 0) ui.save.textContent = `You save ${money(savings)} vs. buying the buckle and strap separately`;

    ui.add.disabled = !variant;
    ui.addLabel.textContent = variant ? `Add to cart · ${money(variant.price)}` : 'Add to cart';
    this.classList.toggle('has-pick', Boolean(buckle));
  }

  async addToCart() {
    if (!this.variant) return;
    const { ui } = this;
    const id = this.variant.id;
    ui.add.disabled = true;
    ui.add.setAttribute('aria-busy', 'true');
    ui.addLabel.textContent = 'Adding…';
    ui.error.hidden = true;

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((c) => c.dataset.sectionId)
      .filter(Boolean);
    try {
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sections.join(',') }),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'We could not add this belt.');
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      this.dispatchEvent(
        new CartAddEvent({}, String(id), {
          source: 'ob-builder',
          itemCount: cart.item_count,
          variantId: String(id),
          sections: json.sections,
        })
      );
      ui.addLabel.textContent = 'Added to cart';
      setTimeout(() => this.renderSummary(), 1800);
    } catch (e) {
      ui.error.textContent = `${e.message} Try again, or choose another strap.`;
      ui.error.hidden = false;
      this.renderSummary();
    }
    ui.add.removeAttribute('aria-busy');
  }
}

if (!customElements.get('ob-builder')) customElements.define('ob-builder', ObBuilder);
